export function activityPostObject(element: any) {
  return {
    id: element.id,
    title: element.attributes.Title,
    content: element.attributes.Content,
    date: element.attributes.PublishDate,
    readingDuration: element.attributes.ReadingDuration,
    featureImage: element.attributes.FeaturedImage.data.attributes.url,
    carouselImage: element.attributes.CarouselImage.data.attributes.url,
    iconImage: element.attributes.IconImage.data.attributes.url,
    tag: element?.attributes?.tags?.data?.[0]?.attributes.Name,
    recommendedTag:
      element?.attributes?.highlighted_tags?.data?.[0]?.attributes?.Name,
  };
}

//recipe post object
export function recipePostObject(element: any) {
  return {
    id: element.id,
    title: element.attributes.Title,
    content: element.attributes.Content,
    date: element.attributes.PublishDate,
    readingDuration: element.attributes.ReadingDuration,
    featureImage: element.attributes.FeaturedImage.data.attributes.url,
    carouselImage: element.attributes.CarouselImage.data.attributes.url,
    iconImage: element.attributes.IconImage.data.attributes.url,
    recommendedTag:
      element?.attributes?.highlighted_tags?.data?.[0]?.attributes?.Name,
    recipeImageArray: element?.attributes?.recipe_image_formats?.data,
    recipeType: element?.attributes?.RecipeType,
    recipeCategory:
      element?.attributes?.recipe_categories?.data?.[0]?.attributes?.Title,
  };
}

//blog post object
export function blogPostObject(element: any) {
  return {
    id: element.id,
    title: element.attributes.Title,
    content: element.attributes.Content,
    date: element.attributes.PublishDate,
    readingDuration: element.attributes.ReadingDuration,
    featureImage: element.attributes.FeaturedImage.data.attributes.url,
    carouselImage: element.attributes.CarouselImage.data.attributes.url,
    iconImage: element.attributes.IconImage.data.attributes.url,
    blogCategory:
      element?.attributes?.blog_categories?.data?.[0]?.attributes?.Name,
    tag: element?.attributes?.tags?.data?.[0]?.attributes.Name,
    recommendedTag:
      element?.attributes?.highlighted_tags?.data?.[0]?.attributes?.Name,
  };
}

// Strip HTML tags from content
export function stripHTML(content: string): string {
  const regex = /(<([^>]+)>)/gi;
  return content.replace(regex, "");
}

// Truncate text with ellipsis
export function truncateText(text: string, maxLength: number): string {
  if (!text) {
    return "-";
  }
  return text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;
}

// Format reading duration
export function formatReadingDuration(duration: number): string {
  return `${duration} mins`;
}

// Calculate days between two dates
export function calculateDaysBetween(date1: string, date2: string): number {
  const daysBetween = new Date(date1).getTime() - new Date(date2).getTime();
  return Math.floor(daysBetween / (1000 * 60 * 60 * 24));
}

// Check if date is today
export function isDateToday(date: string, currentDate: string): boolean {
  return date === currentDate;
}

// Get recommended categories from data arrays
export function getRecommendedCategories(dataArrays: {
  eats?: any[];
  mind?: any[];
  body?: any[];
  plays?: any[];
  recipes?: any[];
  activities?: any[];
}) {
  return {
    recommendedEat:
      dataArrays.eats?.filter(
        (item) => item.recommendedTag === "Recommended",
      ) || [],
    recommendedMind:
      dataArrays.mind?.filter(
        (item) => item.recommendedTag === "Recommended",
      ) || [],
    recommendedBody:
      dataArrays.body?.filter(
        (item) => item.recommendedTag === "Recommended",
      ) || [],
    recommendedPlay:
      dataArrays.plays?.filter(
        (item) => item.recommendedTag === "Recommended",
      ) || [],
    recommendRecipe:
      dataArrays.recipes?.filter(
        (item) => item.recommendedTag === "Recommended",
      ) || [],
    recommendedActivities:
      dataArrays.activities?.filter(
        (item) => item.recommendedTag === "Recommended",
      ) || [],
  };
}

// Get age-categorized data from arrays
export function getAgeCategories(dataArrays: {
  eats?: any[];
  mind?: any[];
  body?: any[];
  plays?: any[];
  activities?: any[];
}) {
  return {
    // Eat categories
    tagTeenEat: dataArrays.eats?.filter((item) => item.tag === "Teen") || [],
    tagPreteenEat:
      dataArrays.eats?.filter((item) => item.tag === "Preeteen") || [],
    tagKidEat: dataArrays.eats?.filter((item) => item.tag === "Kids") || [],
    tagToddlerEat:
      dataArrays.eats?.filter((item) => item.tag === "Toddler") || [],

    // Mind categories
    tagTeenMind: dataArrays.mind?.filter((item) => item.tag === "Teen") || [],
    tagPreteenMind:
      dataArrays.mind?.filter((item) => item.tag === "Preeteen") || [],
    tagKidMind: dataArrays.mind?.filter((item) => item.tag === "Kids") || [],
    tagToddlerMind:
      dataArrays.mind?.filter((item) => item.tag === "Toddler") || [],

    // Play categories
    tagTeenPlay: dataArrays.plays?.filter((item) => item.tag === "Teen") || [],
    tagPreteenPlay:
      dataArrays.plays?.filter((item) => item.tag === "Preeteen") || [],
    tagKidPlay: dataArrays.plays?.filter((item) => item.tag === "Kids") || [],
    tagToddlerPlay:
      dataArrays.plays?.filter((item) => item.tag === "Toddler") || [],

    // Body categories
    tagTeenBody: dataArrays.body?.filter((item) => item.tag === "Teen") || [],
    tagPreteenBody:
      dataArrays.body?.filter((item) => item.tag === "Preeteen") || [],
    tagKidBody: dataArrays.body?.filter((item) => item.tag === "Kids") || [],
    tagToddlerBody:
      dataArrays.body?.filter((item) => item.tag === "Toddler") || [],

    // Activity categories
    tagTeen: dataArrays.activities?.filter((item) => item.tag === "Teen") || [],
    tagPreteen:
      dataArrays.activities?.filter((item) => item.tag === "Preeteen") || [],
    tagKid: dataArrays.activities?.filter((item) => item.tag === "Kids") || [],
    tagToddler:
      dataArrays.activities?.filter((item) => item.tag === "Toddler") || [],
  };
}

// Get age-filtered data for a specific category
export function getAgeFilteredData(
  categoryTitle: string,
  ageGroups: {
    toddlers: boolean;
    kids: boolean;
    preteens: boolean;
    teens: boolean;
  },
  ageCategories: any,
): any[] {
  const categoryData = {
    Eat: {
      toddlers: ageCategories.tagToddlerEat,
      kids: ageCategories.tagKidEat,
      preteens: ageCategories.tagPreteenEat,
      teens: ageCategories.tagTeenEat,
    },
    Mind: {
      toddlers: ageCategories.tagToddlerMind,
      kids: ageCategories.tagKidMind,
      preteens: ageCategories.tagPreteenMind,
      teens: ageCategories.tagTeenMind,
    },
    Body: {
      toddlers: ageCategories.tagToddlerBody,
      kids: ageCategories.tagKidBody,
      preteens: ageCategories.tagPreteenBody,
      teens: ageCategories.tagTeenBody,
    },
    Play: {
      toddlers: ageCategories.tagToddlerPlay,
      kids: ageCategories.tagKidPlay,
      preteens: ageCategories.tagPreteenPlay,
      teens: ageCategories.tagTeenPlay,
    },
  };

  if (categoryData[categoryTitle]) {
    for (const [ageGroup, isSelected] of Object.entries(ageGroups)) {
      if (isSelected) {
        return categoryData[categoryTitle][ageGroup] || [];
      }
    }
  }
  return [];
}

// Get activity age-filtered data
export function getActivityAgeFilteredData(
  ageGroups: {
    toddlers: boolean;
    kids: boolean;
    preteens: boolean;
    teens: boolean;
  },
  ageCategories: any,
): any[] {
  if (ageGroups.toddlers) {
    return ageCategories.tagToddler || [];
  }
  if (ageGroups.teens) {
    return ageCategories.tagTeen || [];
  }
  if (ageGroups.preteens) {
    return ageCategories.tagPreteen || [];
  }
  if (ageGroups.kids) {
    return ageCategories.tagKid || [];
  }
  return [];
}

// Parse deep link category and find matching item
export function parseDeepLinkCategory(
  categoryId: string,
  categories: any[],
): any | null {
  return categories.find(
    (cat) => cat.title.toLowerCase() === categoryId.toLowerCase(),
  );
}

// Generate age group state from subcategory
export function generateAgeGroupState(subCategory?: string) {
  const defaultState = {
    toddlers: true,
    kids: false,
    teens: false,
    preteens: false,
  };

  if (!subCategory) {
    return defaultState;
  }

  const ageGroup = subCategory.toLowerCase();
  return {
    toddlers: ageGroup === "toddlers",
    kids: ageGroup === "kids",
    teens: ageGroup === "teens",
    preteens: ageGroup === "preteens",
  };
}

// Map recipe subcategory to state
export function mapRecipeSubCategoryToState(subCategory: string) {
  const lowerSubCategory = subCategory.toLowerCase();

  if (lowerSubCategory === "healthymeals") {
    return {
      healthyMeal: true,
      snackBeverage: false,
      recipeTitle: "Healthy Meals",
    };
  } else if (lowerSubCategory === "snacksbeverages") {
    return {
      healthyMeal: false,
      snackBeverage: true,
      recipeTitle: "Snacks & Beverages",
    };
  }

  return {
    healthyMeal: false,
    snackBeverage: false,
    recipeTitle: "Wellness Kitchen",
  };
}

// Check if data array is loading
export function isDataLoading(dataArray: any[] | string): boolean {
  return !dataArray || (Array.isArray(dataArray) && dataArray.length === 0);
}

// Validate recipe title and normalize
export function validateRecipeTitle(title: string): string {
  if (title === "Snacks and Beverage") {
    return title + "s";
  }
  return title;
}

// Find child by ID from association array
export function findChildById(
  childId: string | number,
  associations: any[],
): any | null {
  if (
    !childId ||
    !associations ||
    !Array.isArray(associations) ||
    associations.length === 0
  ) {
    return null;
  }

  const normalizedChildId = childId.toString().trim();
  if (!normalizedChildId) {
    return null;
  }

  return (
    associations.find(
      (child) => child && child.id && child.id.toString() === normalizedChildId,
    ) || null
  );
}

// Validate deeplink route parameters
export function validateDeeplinkParams(route: any): {
  isValid: boolean;
  childId?: string;
} {
  if (!route || !route.params) {
    return { isValid: false };
  }

  const { childId } = route.params;

  if (!childId) {
    return { isValid: false };
  }

  const normalizedChildId = childId.toString().trim();
  if (!normalizedChildId || normalizedChildId === "0") {
    return { isValid: false };
  }

  return { isValid: true, childId: normalizedChildId };
}

// Common user management functions used across multiple screens

/**
 * Parse association element to dropdown format (used in multiple screens)
 * @param element - Association element
 * @returns Formatted dropdown item
 */
export function parseAssociationObject(element: any): {
  value: string | number;
  label: string;
  type: "0" | "1";
  class?: string;
  role?: number;
} {
  try {
    return {
      value: element.id,
      label: element.firstName,
      type: "0",
      class: element.class,
      role: element.role,
    };
  } catch (error) {
    console.error("Error parsing association object:", error);
    return {
      value: "",
      label: "Unknown",
      type: "0",
    };
  }
}

/**
 * Parse child organization data for analytics (used in multiple screens)
 * @param element - Association element
 * @returns Formatted org string for analytics
 */
export function parseChildOrganization(element: any): string {
  try {
    return "|" + Number(element.org_id) + "|";
  } catch (error) {
    console.error("Error parsing child organization:", error);
    return "|0|";
  }
}

const possibleFormats = [
  "DD-MM-YYYY",
  "YYYY-MM-DD",
  "MM-DD-YYYY",
  "DD/MM/YYYY",
  "YYYY/MM/DD",
  "MM/DD/YYYY",
  "DD MMM YYYY",
  "MMM DD, YYYY",
  "YYYY MMM DD",
  // add any other formats you expect
];

export function isCallAllowed(dateString: string): boolean {
  const dateGmt = new Date(dateString);

  if (isNaN(dateGmt.getTime())) {
    console.error("Error: Could not parse the date string:", dateString);
    return false;
  }
  const istOffsetMs = 5 * 60 * 60 * 1000 + 30 * 60 * 1000;
  const istTime = new Date(dateGmt.getTime() + istOffsetMs);
  const day = istTime.getUTCDay();
  // getUTCHours(): 0-23
  const hour = istTime.getUTCHours();
  const isWeekday = day >= 1 && day <= 5;

  const isWithinWorkingHours = hour >= 10 && hour < 18;

  return isWeekday && isWithinWorkingHours;
}

export const errorMessgageMap = {
  404: "Looks like there’s no information available for this request at the moment. You can go back to the report or choose a different parameter.",
  500: "Oops, something went wrong on my side. Please try again in a moment.",
};

export const NeurologicalMap = {
  Neurological_Lethargy: "Lethargy",
  Neurological_Slurring_of_Speech: "Slurring of Speech",
  Neurological_Abnormal_Body_Tone_increased: "Abnormal Body Tone Increased",
  Neurological_Abnormal_Body_Tone_Decreased: "Abnormal Body Tone Decreased",
  Neurological_Abnormal_Body_Tone_: "Abnormal Body Tone",
};

export const getCurrentIndianDatetime = () => {
  const now = new Date();

  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

  const indiaTime = new Date(now.getTime() + IST_OFFSET_MS);

  const result = indiaTime.toISOString().replace("Z", "") + "000";
  return result;
};

export const getGreeting = (): string => {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Good Morning";
  } else if (hour < 17) {
    return "Good Afternoon";
  } else {
    return "Good Evening";
  }
};

const quadrantBaseData = {
  // Q1 Data (Upper Right) - RAW coordinates
  Q1: [
    { id: "Q1_8", pathKey: "P8", x: 25, y: 175, label: " 8 ", isLower: false },
    { id: "Q1_7", pathKey: "P7", x: 22, y: 142, label: " 7", isLower: false },
    { id: "Q1_6", pathKey: "P6", x: 27, y: 107, label: " 6 ", isLower: false },
    { id: "Q1_5", pathKey: "P5", x: 37, y: 83, label: " 5 ", isLower: false },
    { id: "Q1_4", pathKey: "P4", x: 50, y: 62, label: " 4", isLower: false },
    { id: "Q1_3", pathKey: "P3", x: 65, y: 45, label: " 3 ", isLower: false },
    { id: "Q1_2", pathKey: "P2", x: 85, y: 25, label: " 2 ", isLower: false },
    { id: "Q1_1", pathKey: "P1", x: 115, y: 18, label: " 1 ", isLower: false },
    {
      id: "Q1_e",
      pathKey: "D-E1",
      x: 73,
      y: 105,
      label: " e ",
      isLower: false,
    },
    { id: "Q1_d", pathKey: "D-D1", x: 81, y: 93, label: " d ", isLower: false },
    { id: "Q1_c", pathKey: "D-C1", x: 88, y: 78, label: " c ", isLower: false },
    {
      id: "Q1_b",
      pathKey: "D-B1",
      x: 103,
      y: 67,
      label: " b ",
      isLower: false,
    },
    {
      id: "Q1_a",
      pathKey: "D-A1",
      x: 122,
      y: 64,
      label: " a ",
      isLower: false,
    },
  ],
  // Q2 Data (Upper Left) - RAW coordinates
  Q2: [
    {
      id: "Q2_a",
      pathKey: "D-A2",
      x: 143,
      y: 63,
      label: " a ",
      isLower: false,
    },
    { id: "Q2_b", pathKey: "D-B2", x: 159, y: 67, label: " b", isLower: false },
    { id: "Q2_c", pathKey: "D-C2", x: 173, y: 78, label: " c", isLower: false },
    { id: "Q2_d", pathKey: "D-D2", x: 182, y: 94, label: "d ", isLower: false },
    {
      id: "Q2_e",
      pathKey: "D-E2",
      x: 189,
      y: 106,
      label: " e ",
      isLower: false,
    },
    {
      id: "Q2_1",
      pathKey: "P1-2",
      x: 147,
      y: 18,
      label: " 1 ",
      isLower: false,
    },
    { id: "Q2_2", pathKey: "P2-2", x: 177, y: 25, label: "2", isLower: false },
    {
      id: "Q2_3",
      pathKey: "P3-2",
      x: 197,
      y: 45,
      label: " 3 ",
      isLower: false,
    },
    {
      id: "Q2_4",
      pathKey: "P4-2",
      x: 212,
      y: 62,
      label: " 4 ",
      isLower: false,
    },
    { id: "Q2_5", pathKey: "P5-2", x: 225, y: 83, label: " 5", isLower: false },
    {
      id: "Q2_6",
      pathKey: "P6-2",
      x: 235,
      y: 107,
      label: " 6 ",
      isLower: false,
    },
    {
      id: "Q2_7",
      pathKey: "P7-2",
      x: 240,
      y: 142,
      label: " 7 ",
      isLower: false,
    },
    {
      id: "Q2_8",
      pathKey: "P8-2",
      x: 237,
      y: 175,
      label: " 8 ",
      isLower: false,
    },
  ],
};

export const quadrantToothData = {
  ...quadrantBaseData,

  Q3: quadrantBaseData["Q2"].map((t) => ({
    ...t,
    id: t.id.replace("Q2", "Q3"),
    isLower: true,
  })),

  Q4: quadrantBaseData["Q1"].map((t) => ({
    ...t,
    id: t.id.replace("Q1", "Q4"),
    isLower: true,
  })),
};

export function getLatestIdForProgram(
  data: Record<string, any>,
  targetProgramId: number = 6,
): number | null {
  let targetId: number | null = null;
  let latestTime = "";

  for (const key in data) {
    const item = data[key];
    if (item.program_id === targetProgramId) {
      if (item.created_time > latestTime) {
        latestTime = item.created_time;
        targetId = item.id;
      }
    }
  }

  return targetId;
}
